import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { EnfafButtonComponent } from '../../../../../components/enfaf-button/enfaf-button.component';
import { EnfafInputFileDraggerLightComponent } from '../../../../../components/enfaf-input-file-dragger-light/enfaf-input-file-dragger-light.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { CustomFile } from '../../../../../models/interfaces/custom_file.interface';

@Component({
  selector: 'app-modal-god-add-sales',
  standalone: true,
  imports: [CommonModule, MatDialogModule, EnfafModalComponent, EnfafButtonComponent, EnfafInputFileDraggerLightComponent],
  templateUrl: './modal-god-add-sales.component.html',
  styleUrl: './modal-god-add-sales.component.scss'
})
export class ModalGodAddSalesComponent {
  private readonly dialogRef = inject(MatDialogRef<ModalGodAddSalesComponent>);
  private readonly apiService = inject(ApiService);
  private readonly toastService = inject(ToastService);

  protected readonly acceptedTypes = '.csv,.xls,.xlsx';
  protected readonly selectedFile = signal<File | null>(null);
  protected readonly isSubmitting = signal(false);

  /**
   * The two halves of what the upload did: what went in, and what was left out with the reason.
   * The import saves every row it can, so both can be filled at the same time.
   */
  protected readonly importedMessages = signal<string[]>([]);
  protected readonly problemMessages = signal<string[]>([]);
  protected readonly problemsHeadline = signal<string>('');
  protected readonly feedbackTone = signal<'error' | 'notice'>('error');

  /**
   * Whether any row of any upload made it into the database. The modal stays open while there is
   * something to read, so closing it must still tell the page behind to reload its sales.
   */
  private hasSavedSales = false;

  protected get isSaveDisabled(): boolean {
    return !this.selectedFile() || this.isSubmitting();
  }

  onFilesUpdated(files: CustomFile[]): void {
    const file = files?.[0]?.file;
    this.selectedFile.set(file ?? null);
    this.clearFeedback();
  }

  async downloadExample(): Promise<void> {
    try {
      await this.apiService.downloadGodSalesExampleCsv();
    } catch {
      this.toastService.presentErrorToast('Hubo un error descargando el ejemplo');
    }
  }

  async save(): Promise<void> {
    const file = this.selectedFile();
    if (!file) return;

    try {
      this.isSubmitting.set(true);
      this.clearFeedback();
      const formData = new FormData();
      formData.append('file', file, file.name);

      const response = await firstValueFrom(this.apiService.uploadAmbassadorGodSalesFile(formData));
      this.showImportResult(response);
    } catch (errorResponse: any) {
      // A rejected upload answers with the same body as an accepted one, under `error`
      this.showImportResult(errorResponse?.error);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  close(): void {
    this.dialogRef.close(this.hasSavedSales);
  }

  /**
   * Render what the upload did.
   *
   * The modal only closes when there is nothing left to read: every row went in and none was
   * questioned. In any other case it stays open with the list, because closing it with a toast
   * would hide the rows that were left out and why.
   */
  private showImportResult(body: any): void {
    const details = body?.details;
    const imported: string[] = Array.isArray(details?.imported) ? details.imported : [];
    const problems: string[] = Array.isArray(details?.problems) ? details.problems : Array.isArray(details?.summary) ? details.summary : [];
    const headline: string = details?.headline || body?.message || 'Hubo un error importando las ventas';
    const saved = Number(body?.data?.saved ?? 0);

    if (saved > 0) this.hasSavedSales = true;

    if (!problems.length && saved > 0) {
      this.toastService.presentSuccessToast(body?.message || headline);
      this.dialogRef.close(body ?? true);
      return;
    }

    this.feedbackTone.set(details?.severity === 'notice' ? 'notice' : 'error');
    this.problemsHeadline.set(headline);
    this.importedMessages.set(imported);
    // A body with no readable detail at all still has to say something
    this.problemMessages.set(problems.length ? problems : [headline]);
    this.toastService.presentErrorToast(headline, 6000);
  }

  private clearFeedback(): void {
    this.importedMessages.set([]);
    this.problemMessages.set([]);
    this.problemsHeadline.set('');
    this.feedbackTone.set('error');
  }
}
